import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import {
  CheckCircle2Icon,
  CircleAlertIcon,
  PencilIcon,
  UsersIcon,
  HistoryIcon,
} from 'lucide-react';
import appHistoryCopy, { type AppHistoryCopyKey } from '@/i18n/ns/app-history';
import {
  HISTORY_FILTERS,
  filterHistory,
  groupByDay,
  initials,
  type HistoryEvent,
  type HistoryFilter,
  type HistoryOrigin,
} from './history-model';
import { historyImageOwner } from '../media/media-model';
import { ObservedImageBlock, type RenderImage } from '../media/media-parts';
import './history-tab.css';
import { Button } from '@/components/ui/button';
import { OriginTag, Segmented } from '@/components/ui/kit';

const FILTER_KEY: Record<HistoryFilter, AppHistoryCopyKey> = {
  all: 'filterAll',
  entries: 'filterEntries',
  approvals: 'filterApprovals',
  changes: 'filterChanges',
  publishes: 'filterPublishes',
};

export type HistoryTabProps = {
  events: readonly HistoryEvent[];
  filter: HistoryFilter;
  onFilter: (filter: HistoryFilter) => void;
  /** Opens a read-only approval record by id; absent when the viewer may not open records. */
  onViewRecord?: (recordId: string) => void;
  /** Resolves a skipped viewer-mode run: choose who it runs as / change in chat. */
  onSkippedAction?: (eventId: string, action: 'choose-run-as' | 'change-in-chat') => void;
  now: Date;
  locale?: string;
  /** APP's shared image primitive; without it an observed image is not drawn. */
  renderImage?: RenderImage;
  /** Whether the viewer sees every entry (editor) or only their own (the note says so). */
  showOwnEntriesNote?: boolean;
};

export function HistoryTab(props: HistoryTabProps) {
  const copy = useNamespaceCopy(appHistoryCopy);
  const { events, filter, now, locale } = props;
  const shown = filterHistory(events, filter);
  const days = groupByDay(shown, now);
  const time = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' });
  const dayLabel = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  return (
    <div className="pc-history" data-testid="app-history">
      <div className="pc-history-bar">
        <Segmented
          label={copy.filtersLabel}
          value={filter}
          onChange={props.onFilter}
          options={HISTORY_FILTERS.map((id) => ({ value: id, label: copy[FILTER_KEY[id]] }))}
        />
        {props.showOwnEntriesNote !== false && <p className="pc-history-note">{copy.note}</p>}
      </div>
      {days.length === 0 && (
        <p className="pc-history-empty" data-testid="history-empty">
          {events.length === 0 ? copy.empty : copy.emptyFiltered}
        </p>
      )}
      {days.map((day) => (
        <section key={day.key} className="pc-history-day" data-testid="history-day">
          <h3>
            {day.offset === 0
              ? copy.today
              : day.offset === 1
                ? formatCopy(copy.yesterday, { day: dayLabel.format(new Date(day.at)) })
                : dayLabel.format(new Date(day.at))}
          </h3>
          <ol>
            {day.events.map((event) => (
              <HistoryRow
                key={event.id}
                event={event}
                time={time.format(new Date(event.at))}
                props={props}
              />
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

function OriginChip({ origin }: { origin: HistoryOrigin | undefined }) {
  const copy = useNamespaceCopy(appHistoryCopy);
  if (!origin || origin.kind === 'builder') return null;
  return (
    <span data-origin={origin.kind}>
      <OriginTag label={formatCopy(copy.viaOrigin, { label: origin.label })} />
    </span>
  );
}

function describe(event: HistoryEvent, copy: Readonly<Record<AppHistoryCopyKey, string>>) {
  switch (event.kind) {
    case 'entry':
      return {
        title: event.summary,
        detail: event.detail ?? null,
        person: event.actor?.name ?? null,
      };
    case 'approval':
      return {
        title: formatCopy(event.outcome === 'approved' ? copy.approvedTitle : copy.declinedTitle, {
          what: event.what,
        }),
        detail: [
          formatCopy(copy.approvalDetail, { proposer: event.proposedBy, decider: event.decidedBy }),
          event.written
            ? formatCopy(copy.approvalWritten, {
                done: String(event.written.done),
                total: String(event.written.total),
              })
            : null,
        ]
          .filter(Boolean)
          .join(' · '),
        person: null,
      };
    case 'change':
      return {
        title: formatCopy(copy.changeTitle, {
          title: event.title,
          version: String(event.draftVersion),
        }),
        detail: [event.detail, event.for ? formatCopy(copy.changeFor, { who: event.for }) : null]
          .filter(Boolean)
          .join(' · '),
        person: null,
      };
    case 'publish':
      return {
        title: formatCopy(copy.publishTitle, {
          version: String(event.version),
          audience: event.audience,
        }),
        detail: [
          event.requestedBy && event.actor
            ? formatCopy(copy.publishRequested, {
                who: event.actor.name,
                requester: event.requestedBy,
              })
            : null,
          formatCopy(copy.publishDetail, { people: String(event.people) }),
        ]
          .filter(Boolean)
          .join(' · '),
        person: null,
      };
    case 'unpublish':
      return {
        title: formatCopy(copy.unpublishTitle, { version: String(event.version) }),
        detail: null,
        person: null,
      };
    case 'restore':
      return {
        title: formatCopy(copy.restoreTitle, { what: event.what }),
        detail: formatCopy(copy.restoreTarget, { target: event.target }),
        person: null,
      };
    case 'schema':
      return { title: event.title, detail: event.detail ?? null, person: null };
    case 'refresh':
      return {
        title: event.ok ? copy.refreshOk : copy.refreshFailed,
        detail: event.detail ?? null,
        person: null,
      };
    case 'source_view':
      return {
        title: formatCopy(copy.sourceViewed, { path: event.path }),
        detail: null,
        person: null,
      };
    case 'skipped_run':
      return {
        title: formatCopy(copy.skippedTitle, { run: event.run, part: event.part }),
        detail: formatCopy(copy.skippedBody, { system: event.system, part: event.part }),
        person: null,
      };
    case 'operation_blocked':
      return {
        title: formatCopy(
          event.reason === 'approval'
            ? copy.blockedApproval
            : event.reason === 'budget'
              ? copy.blockedBudget
              : copy.blockedProvider,
          { title: event.title },
        ),
        detail: null,
        person: null,
      };
  }
}

function HistoryRow({
  event,
  time,
  props,
}: {
  event: HistoryEvent;
  time: string;
  props: HistoryTabProps;
}) {
  const copy = useNamespaceCopy(appHistoryCopy);
  const view = describe(event, copy);
  return (
    <li className="pc-history-row" data-testid="history-row" data-kind={event.kind}>
      <time dateTime={event.at}>{time}</time>
      <span className="pc-history-avatar" aria-hidden="true">
        {view.person ? initials(view.person) : <KindIcon kind={event.kind} />}
      </span>
      <div className="pc-history-body">
        {view.person && <strong>{view.person}</strong>}
        <span className="pc-history-title">{view.title}</span>
        {view.detail && <small>{view.detail}</small>}
        {event.kind === 'entry' && event.image && props.renderImage && (
          <ObservedImageBlock
            image={event.image}
            owner={historyImageOwner(event.id)}
            renderImage={props.renderImage}
            formatAt={(iso) =>
              new Intl.DateTimeFormat(props.locale, {
                dateStyle: 'medium',
                timeStyle: 'short',
              }).format(new Date(iso))
            }
          />
        )}
        <OriginChip origin={event.origin} />
      </div>
      {event.kind === 'approval' && props.onViewRecord && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          data-testid="history-view-record"
          onClick={() => props.onViewRecord?.(event.recordId)}
        >
          {copy.viewRecord}
        </Button>
      )}
      {event.kind === 'skipped_run' && props.onSkippedAction && (
        <span className="pc-history-actions">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => props.onSkippedAction?.(event.id, 'choose-run-as')}
          >
            {copy.chooseRunAs}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => props.onSkippedAction?.(event.id, 'change-in-chat')}
          >
            {copy.changeInChat}
          </Button>
        </span>
      )}
    </li>
  );
}

function KindIcon({ kind }: { kind: HistoryEvent['kind'] }) {
  const props = { size: 14, strokeWidth: 1.75 } as const;
  switch (kind) {
    case 'approval':
      return <CheckCircle2Icon {...props} />;
    case 'publish':
    case 'unpublish':
      return <UsersIcon {...props} />;
    case 'skipped_run':
    case 'operation_blocked':
      return <CircleAlertIcon {...props} />;
    case 'restore':
    case 'refresh':
      return <HistoryIcon {...props} />;
    default:
      return <PencilIcon {...props} />;
  }
}
